import { ChangeDetectionStrategy, Component, input, model, output } from '@angular/core';
import { ButtonComponent } from '../button/button.component';
import { FIELD_LABEL } from '../form/field-styles';
import { uniqueId } from '../form/ids';

/**
 * Dato ereditato dalla commessa (FR-055): finché il cantiere non ha un valore proprio mostra
 * quello della commessa con "Ereditato dalla commessa" e "Usa un valore diverso"; con un valore
 * proprio mostra il campo proiettato (`ng-content`) e "Torna al valore della commessa".
 */
@Component({
  selector: 'bs-inherited-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent],
  host: { class: 'flex flex-col gap-1.5' },
  template: `
    @if (overridden()) {
      <ng-content />
      <div>
        <bs-button
          variant="ghost"
          icon="reset"
          [testId]="testId() ? testId() + '-inherit' : null"
          (click)="inherit()"
        >
          Torna al valore della commessa
        </bs-button>
      </div>
    } @else {
      <p [id]="labelId" [class]="labelClass">{{ label() }}</p>
      <div
        class="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-dashed border-stone-300 bg-stone-50 px-3 py-2 dark:border-stone-700 dark:bg-stone-950"
        role="group"
        [attr.aria-labelledby]="labelId"
        [attr.data-testid]="testId()"
      >
        <span
          class="min-w-0 flex-1 text-base text-ink dark:text-stone-50"
          [class.font-mono]="mono()"
          [attr.data-testid]="testId() ? testId() + '-value' : null"
        >
          {{ inheritedValue() || emptyText() }}
        </span>
        <span
          class="inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full bg-stone-100 px-2.5 text-xs font-semibold text-stone-600 ring-1 ring-inset ring-current/20 dark:bg-stone-800 dark:text-stone-300"
        >
          <span class="size-1.5 rounded-full bg-current" aria-hidden="true"></span>
          Ereditato dalla commessa
        </span>
        <bs-button
          variant="secondary"
          [disabled]="disabled()"
          [testId]="testId() ? testId() + '-override' : null"
          (click)="override()"
        >
          Usa un valore diverso
        </bs-button>
      </div>
    }
  `,
})
export class InheritedFieldComponent {
  readonly label = input.required<string>();
  /** Il valore della commessa, già in parole (per esempio "Sì" o il CIG). */
  readonly inheritedValue = input<string | null>(null);
  readonly emptyText = input('Non indicato nella commessa');
  readonly mono = input(false);
  readonly disabled = input(false);
  readonly testId = input<string | null>(null);
  /** Il cantiere ha un valore proprio. */
  readonly overridden = model(false);
  /** Si è tornati al valore della commessa: il valore proprio va scartato. */
  readonly inherited = output<void>();

  protected readonly labelId = uniqueId('bs-inherited-field');
  protected readonly labelClass = FIELD_LABEL;

  protected override(): void {
    this.overridden.set(true);
  }

  protected inherit(): void {
    this.overridden.set(false);
    this.inherited.emit();
  }
}
